// src/Components/Admin/Users.js

import React, { useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import { useUsersManage } from '../../Hooks/useUsersManage';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

import {
  MenuAddNew,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuIconCancelGrey,
  MenuDownload,
} from '../../assets/icons/CustomIcons';

// --- CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

const EditableCell = ({
  value,
  onChange,
  onFocus,
  autoFocus,
  placeholder,
  isEditing,
}) => {
  if (isEditing) {
    return (
      <TextField
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onFocus={onFocus}
        variant='standard'
        size='small'
        fullWidth
        InputProps={{
          disableUnderline: true,
          style: { fontSize: '0.8rem', color: DARK_BLUE, fontWeight: 'normal' },
        }}
        sx={{
          backgroundColor: LIGHT_BLUE_BG,
          px: 1,
          borderRadius: 1,
          border: `1px solid ${ORANGE_COLOR}`,
          boxSizing: 'border-box',
          '& .MuiInputBase-root': {
            height: '100%',
            fontSize: '0.8rem',
            color: DARK_BLUE,
          },
          '& .MuiInputBase-input': {
            color: DARK_BLUE,
            fontWeight: 'normal',
          },
          '&:hover': { backgroundColor: LIGHT_BLUE_BG },
          '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
        }}
      />
    );
  }
  return (
    <Typography
      variant='body2'
      sx={{
        color: 'text.secondary',
        minHeight: '20px',
        px: 1,
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      {value}
    </Typography>
  );
};

export default function Users({ setIsDirty }) {
  const { state, flags, alerts, actions } = useUsersManage();

  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(flags.isDirty);
    }
  }, [flags.isDirty, setIsDirty]);

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (state.newRows.length > 0 && tableContainerRef.current) {
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [state.newRows.length]);

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  // --- DOWNLOAD HANDLER ---
  const handleDownload = () => {
    if (!state.displayRows || state.displayRows.length === 0) return;

    const now = new Date().toLocaleString();

    const headerRow = [
      'Email',
      'Initial View',
      'Import/Update Markers',
      'Promo',
      'Admin',
      now, // Date and Time in the last column of the first row
    ];

    // Map Data Rows
    const dataRows = state.displayRows.map((row) => {
      const view = state.initialViews.find(
        (v) => String(v.id) === String(row.initialViewId),
      );
      const viewName = view ? view.name : '';

      return [
        row.email,
        viewName,
        row.canUpdateMarkers ? 'TRUE' : 'FALSE',
        row.canAccessPromo ? 'TRUE' : 'FALSE',
        row.isAdmin ? 'TRUE' : 'FALSE',
        '', // Empty cell for the date column in data rows
      ];
    });

    // Create Worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

    // Set column widths
    const wscols = [
      { wch: 35 }, // Email
      { wch: 20 }, // Initial View
      { wch: 25 }, // Import/Update Markers
      { wch: 10 }, // Promo
      { wch: 10 }, // Admin
      { wch: 25 }, // Date/Time Column
    ];
    worksheet['!cols'] = wscols;

    // Create Workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Permitted Users');

    // Trigger Download natively via XLSX
    XLSX.writeFile(
      workbook,
      `PermittedUsers_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const disableDownload = !state.displayRows || state.displayRows.length === 0;
  const isRowEditable = true;
  const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
          mb: 2,
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => actions.handleHeaderAction('ADD_NEW')}
            disabled={false}>
            <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        {/* --- DOWNLOAD BUTTON --- */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleDownload}
            disabled={disableDownload}>
            <MenuDownload
              sx={{
                fontSize: ICON_SIZE,
                opacity: disableDownload ? 0.35 : 1,
                filter: disableDownload ? 'grayscale(100%)' : 'none',
              }}
              iconScale={5}
              iconX={32}
              iconY={38}
              textY={108}
            />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => flags.canSave && actions.handleHeaderAction('SAVE')}
            disabled={!flags.canSave}>
            {flags.canSave ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => flags.canDelete && actions.handleHeaderAction('DELETE')}
            disabled={!flags.canDelete}>
            {flags.canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => flags.canClose && actions.handleHeaderAction('CLOSE')}
            disabled={!flags.canClose}>
            {flags.canClose ? (
              <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>
      </Box>

      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          borderTop: '2px solid #9c27b0',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
        }}>
        {state.loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
            <CircularProgress sx={{ color: '#9c27b0' }} />
          </Box>
        ) : (
          <Table stickyHeader size='small' sx={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  height: '36px',
                  '& th': {
                    backgroundColor: '#f3e5f5',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #9c27b0',
                    py: '4px',
                    ...cellStyle,
                  },
                }}>
                <TableCell padding='checkbox' sx={{ width: '50px' }} />
                <TableCell sx={{ width: '35%' }}>Email</TableCell>
                <TableCell sx={{ width: '20%' }}>Initial View</TableCell>
                <TableCell align='center' sx={{ width: '15%' }}>
                  Import/Update Markers
                </TableCell>
                <TableCell align='center' sx={{ width: '15%' }}>
                  Promo
                </TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Admin
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.displayRows.map((row) => {
                const isSelected = state.selectedIds.includes(row.id);
                const viewExists = state.initialViews.some(
                  (v) => String(v.id) === String(row.initialViewId),
                );
                const safeViewId = viewExists ? String(row.initialViewId) : '';

                return (
                  <TableRow
                    key={row.id}
                    hover={true}
                    selected={isSelected}
                    onClick={() => actions.handleCheckboxChange(row.id)}
                    sx={{
                      cursor: 'pointer',
                      height: '32px',
                      '& td': { py: '4px' },
                      '&:last-child td, &:last-child th': { border: 0 },
                      opacity: 1,
                      '&.Mui-selected': { backgroundColor: 'transparent !important' },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                    }}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        onChange={() => actions.handleCheckboxChange(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()} sx={cellStyle}>
                      <EditableCell
                        value={row.email}
                        placeholder='user@example.com'
                        isEditing={isRowEditable}
                        autoFocus={state.focusedRowId === row.id}
                        onFocus={() => actions.setFocusedRowId(row.id)}
                        onChange={(val) =>
                          actions.handleFieldChange(row.id, 'email', val)
                        }
                      />
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()} sx={cellStyle}>
                      <FormControl fullWidth size='small' variant='standard'>
                        <Select
                          value={safeViewId}
                          onChange={(e) =>
                            actions.handleFieldChange(
                              row.id,
                              'initialViewId',
                              e.target.value,
                            )
                          }
                          disableUnderline
                          displayEmpty
                          MenuProps={{ style: { zIndex: 100020 } }}
                          sx={{
                            fontSize: '0.8rem',
                            backgroundColor: LIGHT_BLUE_BG,
                            border: `1px solid ${ORANGE_COLOR}`,
                            borderRadius: 1,
                            color: DARK_BLUE,
                            fontWeight: 'normal',
                            '& .MuiSelect-select': {
                              paddingTop: '4px',
                              paddingBottom: '4px',
                              paddingLeft: '8px',
                              borderRadius: 1,
                              '&:focus': { backgroundColor: LIGHT_BLUE_BG },
                            },
                            '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                            '& .MuiSvgIcon-root': { color: '#000' },
                          }}>
                          {state.initialViews.map((view) => {
                            const isArchived = !!view.isArchived;
                            const isCurrentlySelected =
                              String(view.id) === String(row.initialViewId);

                            if (!isArchived) {
                              return (
                                <MenuItem
                                  key={view.id}
                                  value={String(view.id)}
                                  sx={{ fontSize: '0.8rem' }}>
                                  {view.name}
                                </MenuItem>
                              );
                            }

                            if (isArchived && isCurrentlySelected) {
                              return (
                                <MenuItem
                                  key={view.id}
                                  value={String(view.id)}
                                  sx={{ display: 'none', fontSize: '0.8rem' }}>
                                  {view.name}
                                </MenuItem>
                              );
                            }
                            return null;
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                      sx={cellStyle}>
                      <Checkbox
                        size='small'
                        checked={!!row.canUpdateMarkers}
                        onChange={(e) =>
                          actions.handleFieldChange(
                            row.id,
                            'canUpdateMarkers',
                            e.target.checked,
                          )
                        }
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>

                    <TableCell
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                      sx={cellStyle}>
                      <Checkbox
                        size='small'
                        checked={!!row.canAccessPromo}
                        onChange={(e) =>
                          actions.handleFieldChange(
                            row.id,
                            'canAccessPromo',
                            e.target.checked,
                          )
                        }
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>

                    <TableCell
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                      sx={cellStyle}>
                      <Checkbox
                        size='small'
                        checked={!!row.isAdmin}
                        onChange={(e) =>
                          actions.handleFieldChange(row.id, 'isAdmin', e.target.checked)
                        }
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <ConfirmDialog
        open={alerts.showUnsaved}
        onClose={() => alerts.setShowUnsaved(false)}
        onConfirm={actions.handleCloseConfirmed}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showDelete}
        onClose={() => alerts.setShowDelete(false)}
        onConfirm={actions.handleConfirmDelete}
        message='This action will permanently delete the selected users from all systems. Are you sure?'
        severity='error'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showLastAdminAlert}
        onClose={() => alerts.setShowLastAdminAlert(false)}
        message={alerts.lastAdminMessage}
        severity='warning'
        autoClose={2000}
        cancelText={null}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      {/* AMENDED: Unified Result Alert specifically for Delete & Save Actions */}
      <ConfirmDialog
        open={alerts.showResultAlert}
        onClose={() => alerts.setShowResultAlert(false)}
        message={alerts.resultMessage}
        severity={alerts.resultSeverity}
        autoClose={3000}
        onConfirm={() => alerts.setShowResultAlert(false)}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
